/**
 * Perfiles: el oficio de una cuenta, DEDUCIDO de sus capacidades.
 *
 * db/044 decidió que el perfil no se guarda en la cuenta —la verdad que la
 * RLS lee son las capacidades, y una etiqueta guardada aparte diría
 * «Capturista» sobre una cuenta a la que alguien ya le marcó una casilla de
 * más—. Así que aquí se calcula, y se calcula en una función pura para que
 * `pruebas/perfiles.prueba.ts` pueda hacerle las preguntas que importan:
 * ¿una cuenta con una capacidad extra sigue diciendo «Capturista»? (no), ¿y
 * una con una de menos? (tampoco).
 *
 * Lo que una persona que reparte trabajo ve es «Capturista de campo»,
 * «Coordinación territorial». Las treinta claves del catálogo quedan detrás
 * de un ajuste fino que casi nadie abre.
 */

export interface Perfil {
  clave: string;
  nombre: string;
  descripcion: string;
  capacidades: Record<string, boolean>;
}

/** Las claves que están en `true`. El orden no importa para comparar. */
export function activas(capacidades: Record<string, boolean>): string[] {
  return Object.entries(capacidades)
    .filter(([, v]) => v === true)
    .map(([k]) => k)
    .sort();
}

/**
 * El perfil cuyas capacidades son EXACTAMENTE las de la cuenta, o `null`
 * cuando no casa con ninguno — y entonces la pantalla dice «a la medida», que
 * es la verdad. Exacto y no «contiene»: una cuenta con las del capturista y
 * además `exportar` no es un capturista, es alguien que puede sacar datos, y
 * esa es justo la diferencia que hay que ver sin abrir nada.
 */
export function perfilDe(
  capacidades: Record<string, boolean>,
  perfiles: Perfil[],
): Perfil | null {
  const mias = activas(capacidades).join(",");
  if (!mias) return null;
  return perfiles.find((p) => activas(p.capacidades).join(",") === mias) ?? null;
}

/**
 * Las capacidades que deja una cuenta al elegir un perfil: las de la
 * plantilla y NADA más. Elegir «Capturista» sobre una cuenta que tenía
 * permisos de más se los quita, porque eso es lo que la persona está
 * diciendo al elegirlo. Se devuelven solo las que quedan en `true`: una clave
 * en `false` explícito no aporta y en db/009 costó un hoyo.
 */
export function aplicarPerfil(perfil: Perfil): Record<string, boolean> {
  return Object.fromEntries(activas(perfil.capacidades).map((k) => [k, true]));
}

/** Cómo se describe una cuenta en una celda: el nombre del perfil o, si no
 *  casa, «a la medida» con cuántas capacidades tiene. */
export function etiquetaDePerfil(
  capacidades: Record<string, boolean>,
  perfiles: Perfil[],
): { texto: string; aMedida: boolean; vacia: boolean } {
  const n = activas(capacidades).length;
  if (n === 0) return { texto: "sin capacidades", aMedida: false, vacia: true };
  const p = perfilDe(capacidades, perfiles);
  if (p) return { texto: p.nombre, aMedida: false, vacia: false };
  return {
    texto: `a la medida · ${n} capacidad${n === 1 ? "" : "es"}`,
    aMedida: true,
    vacia: false,
  };
}
